import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { VrboService } from '@app/_services/vrbo.service';
import { DataReportService } from '@app/_components/data-report/data-report.service';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-vrbo-property',
    templateUrl: './vrbo-property.component.html',
    styleUrls: ['./vrbo-property.component.css'],
    standalone: false
})
export class VRBOPropertyComponent implements OnInit {
  form1!: UntypedFormGroup;
  formLoading = false;

  shortTermRentals: any[] = [];
  selectedShortTermRentalId?: number;
  propertiesTable: Array<{
    shortTermRentalId: number;
    propertyCode: string;
    vrboId?: string;
    vrboShortTermRentalId?: number;
    vrboDisplayId?: string;
    lastSync?: string;
    reportStatus?: string;
  }> = [];

  validationMessages = {
    shortTermRentalId: [{ type: 'required', message: 'Property is required' }]
  };

  constructor(
    private formBuilder: UntypedFormBuilder,
    private messageService: MessageService,
    private vrboService: VrboService,
    private dataReportService: DataReportService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadData();
  }

  buildForm(): void {
    this.form1 = this.formBuilder.group({
      shortTermRentalId: [null, Validators.required]
    });
  }

  get filteredRows() {
    if (!this.selectedShortTermRentalId) {
      return this.propertiesTable;
    }
    return this.propertiesTable.filter(r => Number(r.shortTermRentalId) === Number(this.selectedShortTermRentalId));
  }

  // Load both: all properties and VRBO data reports for VrboPropertySync
  loadData(): void {
    this.formLoading = true;
    forkJoin([
      this.vrboService.findAllPropertiesLite(), // expects { data: { properties: [...] } }
      this.dataReportService.fetchVrboData('VrboPropertySync') // expects { data: { dataReports: [...] } }
    ]).subscribe(
      ([propRes, reportRes]: any[]) => {
        const props: any[] = propRes?.data?.properties || [];
        // Populate dropdown from full properties
        this.shortTermRentals = props;

        const reports: any[] = reportRes?.data?.qualityTests || reportRes?.data?.dataReports || [];

        // Build latest-per-property map for statuses (Pass=1, Fail=3) and queue COMPLETED/FAILED
        const validStatuses = new Set([1, 3]);
        const validQueues = new Set(['COMPLETED', 'FAILED']);

        const latestByStr: Map<number, any> = new Map();
        for (const r of reports) {
          const strId = Number(r?.shortTermRentalId);
          if (!strId || isNaN(strId)) continue;
          // Must satisfy required status + queue
          if (!validStatuses.has(Number(r?.testResultStatus))) continue;
          if (!validQueues.has(String(r?.queueStatus || '').toUpperCase())) continue;
          const ts = new Date(r?.lastUpdate || r?.createDate || 0).getTime();
          const prev = latestByStr.get(strId);
          const prevTs = prev ? new Date(prev?.lastUpdate || prev?.createDate || 0).getTime() : -1;
          if (!prev || ts > prevTs) {
            latestByStr.set(strId, r);
          }
        }

        // Join with property list and create table rows
        const propById: Map<number, any> = new Map(props.map(p => [Number(p.shortTermRentalId), p]));
        const rows: any[] = [];
        const statusTextMap: { [key: number]: string } = {
          1: 'Pass',
          2: 'Warn',
          3: 'Fail',
          4: 'Info'
        };

        latestByStr.forEach((r, strId) => {
          const p = propById.get(Number(strId));
          if (!p) return;
          rows.push({
            shortTermRentalId: p.shortTermRentalId,
            propertyCode: p.propertyCode,
            vrboId: p.vrboId,
            vrboShortTermRentalId: p.vrboShortTermRentalId,
            vrboDisplayId: p.vrboDisplayId,
            lastSync: r?.lastUpdate || r?.createDate,
            reportStatus: statusTextMap[Number(r?.testResultStatus)] || String(r?.testResultStatus ?? '')
          });
        });

        // Sort rows by propertyCode for stable view
        rows.sort((a, b) => String(a.propertyCode || '').localeCompare(String(b.propertyCode || '')));
        this.propertiesTable = rows;
        this.formLoading = false;
      },
      (err: any) => {
        this.formLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load VRBO property data' });
      }
    );
  }

  onSyncAllProperties(): void {
    this.formLoading = true;
    this.vrboService.saveAllProperties()
      .subscribe(
        (res: any) => {
          this.formLoading = false;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: '' + (res?.message || 'All properties sync triggered'), sticky: false });
        },
        (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'We were unable to sync all properties: ' + (err?.message || err), sticky: true });
        },
        () => {}
      );
  }

  onSaveProperty(): void {
    if (this.form1.invalid) {
      this.form1.markAllAsTouched();
      return;
    }
    const id = Number(this.form1.value.shortTermRentalId);
    const payload = { propertyIds: [id] };
    this.formLoading = true;
    this.vrboService.saveVrboProperty(payload)
      .subscribe(
        (res: any) => {
          this.formLoading = false;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: '' + (res?.message || 'Property sync triggered'), sticky: false });
        },
        (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'We were unable to save : ' + (err?.message || err), sticky: true });
        },
        () => {}
      );
  }
}


